/**
 * §7's keymap and double-tap gesture, as pure functions from an event *description* to a command.
 *
 * Both are here rather than in the screen because the interesting part of each is a decision table,
 * and a decision table in an event handler is a decision table nobody tests. The DOM stays outside:
 * these take plain records, so the web binding is `keyToCommand({key: e.key, …})` and a TV binding
 * is the same call with a different table in front of it.
 *
 * §7 is explicit that double-tap-skip is the one control with **no D-pad analogue** — it is
 * *mirrored* on TV (D-pad left/right with an accelerating repeat), not shared. What is genuinely
 * shared is the vocabulary: {@link PlayerCommand} is what both bindings produce, so the screen has
 * one command handler rather than one per input device.
 */

// ---------------------------------------------------------------------------
// The vocabulary
// ---------------------------------------------------------------------------

export type PlayerCommand =
  | { readonly kind: 'toggle-play' }
  /**
   * Not reachable from {@link keyToCommand} or {@link decideTap} — every *human* input on this page is
   * a toggle, because the person pressing it can see whether the video is playing. The two one-way
   * arms below exist for the input that cannot: an OS media button sends the action it believes is
   * needed, from a state it was told at some point in the past, so a `play` can arrive while playback
   * is already running and folding it onto the toggle would pause it. See `playback/mediaSession.ts`.
   */
  | { readonly kind: 'play' }
  | { readonly kind: 'pause' }
  | { readonly kind: 'seek-by'; readonly seconds: number }
  /** `0`–`9`: jump to a percentage of the duration. */
  | { readonly kind: 'seek-fraction'; readonly fraction: number }
  /**
   * An absolute position: a chapter, a description timestamp, a comment's timecode, an OS scrub. Also
   * 4c's, and for the same reason the two arms above are — `seek-fraction` cannot express a *time*,
   * and the three call sites that already seek absolutely were reaching past the one dispatcher to do
   * it.
   */
  | { readonly kind: 'seek-to'; readonly seconds: number }
  /** §7 — only meaningful while paused; the caller supplies the frame duration. */
  | { readonly kind: 'frame-step'; readonly frames: number }
  | { readonly kind: 'volume-by'; readonly delta: number }
  | { readonly kind: 'toggle-mute' }
  | { readonly kind: 'toggle-captions' }
  | { readonly kind: 'toggle-fullscreen' }
  /** Steps through `PLAYBACK_RATES`, rather than naming a rate the ladder may not contain. */
  | { readonly kind: 'rate-step'; readonly steps: number };

/** §7's numbers, exported so the on-screen hints cannot claim a different step than the handler. */
export const KEY_SEEK_SEC = 5;
export const KEY_JUMP_SEC = 10;
export const KEY_VOLUME_STEP = 0.05;

// ---------------------------------------------------------------------------
// Keyboard
// ---------------------------------------------------------------------------

export interface KeyEventLike {
  /** `KeyboardEvent.key` — the *character*, so `<` and `>` work on layouts that do not shift them. */
  readonly key: string;
  /**
   * `KeyboardEvent.code` — the key's **place** on the board, which is what the two frame-step keys are
   * addressed by. `,` and `.` sit under the same two fingers on QWERTY, QWERTZ and AZERTY, and
   * on the last of those they type `;` and `:`; a character-only map would put frame stepping somewhere
   * else on every layout that shifts the punctuation. Optional because a TV binding has no such thing.
   */
  readonly code?: string;
  readonly ctrl?: boolean;
  readonly alt?: boolean;
  readonly meta?: boolean;
  readonly shift?: boolean;
  readonly repeat?: boolean;
}

/**
 * What the event landed on. §7 says "skip when `target` is an input", and the three cases below are
 * why that sentence is not implementable as a single check:
 *
 * - `text` — a field being typed into. Every key belongs to it.
 * - `volume` — the volume slider. Its own arrow keys must keep working, or the slider is the one
 *   control on the page that its own keyboard behaviour is stolen from.
 * - `button` — Space and Enter activate it natively; handling them here as well fires twice.
 *
 * The scrub slider is deliberately **not** on this list. It is an `input`, but §7's `←/→` is ±5 s
 * and the native behaviour is ±`step`; letting the native one win would silently make the documented
 * keymap wrong exactly where a user is most likely to try it.
 */
export type KeyTarget = 'text' | 'volume' | 'button' | 'other';

export interface KeyContext {
  /**
   * §7 — `↑/↓` adjust volume *only* while focus is inside the player chrome, because outside it they
   * belong to §9's roving-focus model.
   */
  readonly focusInsidePlayer: boolean;
  readonly target: KeyTarget;
}

/** Held-down keys repeat; a toggle that repeats 30×/s is a flicker, a seek that repeats is a scrub. */
function repeatable(command: PlayerCommand): boolean {
  return command.kind === 'seek-by' || command.kind === 'volume-by';
}

export function keyToCommand(event: KeyEventLike, context: KeyContext): PlayerCommand | null {
  // Never hijack a browser or OS shortcut. `Ctrl+L`, `Alt+←` and `Cmd+M` all collide with the
  // single-letter map below, and the browser's meaning wins every time.
  if (event.ctrl === true || event.alt === true || event.meta === true) return null;
  if (context.target === 'text') return null;

  const command = mapKey(event, context);
  if (command === null) return null;
  if (event.repeat === true && !repeatable(command)) return null;
  return command;
}

function mapKey(event: KeyEventLike, context: KeyContext): PlayerCommand | null {
  const { key } = event;

  /*
   * Frame stepping, by the key's **place** rather than its character. `Shift` has to be clear
   * for this: on QWERTY `<` *is* `Comma` shifted, and it means the rate ladder two arms below — the
   * character map underneath catches a layout on which `,` is itself a shifted key.
   */
  if (event.shift !== true) {
    if (event.code === 'Comma') return { kind: 'frame-step', frames: -1 };
    if (event.code === 'Period') return { kind: 'frame-step', frames: 1 };
  }

  switch (key) {
    case 'ArrowLeft':
      return context.target === 'volume' ? null : { kind: 'seek-by', seconds: -KEY_SEEK_SEC };
    case 'ArrowRight':
      return context.target === 'volume' ? null : { kind: 'seek-by', seconds: KEY_SEEK_SEC };

    case 'ArrowUp':
    case 'ArrowDown': {
      if (!context.focusInsidePlayer) return null;
      // The volume slider's own arrows already do this, and doing it twice moves two steps.
      if (context.target === 'volume') return null;
      return { kind: 'volume-by', delta: key === 'ArrowUp' ? KEY_VOLUME_STEP : -KEY_VOLUME_STEP };
    }

    case ' ':
    case 'Spacebar':
      // A focused button is activated by Space natively; the click handler is the same command.
      return context.target === 'button' ? null : { kind: 'toggle-play' };

    /*
     * The character map for the same two keys, for a layout that shifts them out of reach of the
     * `code` check above.
     *
     * §7 had this as a **paused-only** control — "while playing the key means nothing rather than
     * something surprising" — but that is reversed now: pressing it while playing did mean nothing,
     * which read as frame stepping not existing. Stepping a frame is a request to *look* at one, so
     * the dispatcher pauses first and the key works from either state.
     */
    case ',':
      return { kind: 'frame-step', frames: -1 };
    case '.':
      return { kind: 'frame-step', frames: 1 };

    case '<':
      return { kind: 'rate-step', steps: -1 };
    case '>':
      return { kind: 'rate-step', steps: 1 };

    default:
      break;
  }

  if (key.length === 1 && key >= '0' && key <= '9') {
    return { kind: 'seek-fraction', fraction: Number(key) / 10 };
  }

  switch (key.toLowerCase()) {
    case 'k':
      return { kind: 'toggle-play' };
    case 'j':
      return { kind: 'seek-by', seconds: -KEY_JUMP_SEC };
    case 'l':
      return { kind: 'seek-by', seconds: KEY_JUMP_SEC };
    case 'm':
      return { kind: 'toggle-mute' };
    case 'c':
      return { kind: 'toggle-captions' };
    case 'f':
      return { kind: 'toggle-fullscreen' };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Pointer: §7's double-tap skip
// ---------------------------------------------------------------------------

/** Left 35 % · centre 30 % · right 35 % (§7). */
export type TapZone = 'back' | 'toggle' | 'forward';

export const TAP_SIDE_FRACTION = 0.35;
export const DOUBLE_TAP_WINDOW_MS = 300;
/** A tap that travelled further than this was a drag — a scrub or a scroll, not a gesture. */
export const TAP_MOVE_TOLERANCE_PX = 30;
export const TAP_SKIP_SEC = 10;

export function tapZoneAt(xFraction: number): TapZone {
  if (!Number.isFinite(xFraction)) return 'toggle';
  if (xFraction < TAP_SIDE_FRACTION) return 'back';
  if (xFraction > 1 - TAP_SIDE_FRACTION) return 'forward';
  return 'toggle';
}

export interface TapInput {
  readonly timeMs: number;
  /** 0 at the left edge of the video surface, 1 at the right. */
  readonly xFraction: number;
  /** Distance travelled between `pointerdown` and `pointerup`, in CSS pixels. */
  readonly movedPx: number;
  /**
   * `coarse` is a finger, `fine` is a mouse — and they get different gestures on purpose. §7's
   * zones are a *touch* affordance: on a pointing device a click anywhere means play/pause and a
   * double click means fullscreen, which is what every video on the web already does. Giving a
   * mouse the touch zones would make two thirds of the picture unclickable for no reason.
   */
  readonly pointer: 'coarse' | 'fine';
}

export interface TapState {
  readonly timeMs: number;
  readonly zone: TapZone;
}

export type TapAction =
  | { readonly kind: 'none' }
  | { readonly kind: 'toggle-play' }
  | { readonly kind: 'skip'; readonly seconds: number }
  | { readonly kind: 'toggle-fullscreen' }
  /** A single tap on a touch side-zone: no skip yet, but the chrome should come back into view. */
  | { readonly kind: 'reveal' };

export interface TapResult {
  /** Feed back in on the next tap. `null` starts a fresh gesture. */
  readonly state: TapState | null;
  readonly action: TapAction;
}

/**
 * One `pointerup`, decided against the previous one.
 *
 * The state is kept after a touch skip rather than cleared, which is what makes a third and fourth
 * tap keep skipping — the common way anyone actually uses this control. It is cleared after a mouse
 * double-click, because there the second click's action is fullscreen and pairing the third with the
 * second would toggle straight back out again.
 */
export function decideTap(previous: TapState | null, tap: TapInput): TapResult {
  if (tap.movedPx > TAP_MOVE_TOLERANCE_PX) return { state: null, action: { kind: 'none' } };

  const isDouble = previous !== null && tap.timeMs - previous.timeMs <= DOUBLE_TAP_WINDOW_MS;
  const zone = tapZoneAt(tap.xFraction);
  const state: TapState = { timeMs: tap.timeMs, zone };

  if (tap.pointer === 'fine') {
    return isDouble
      ? { state: null, action: { kind: 'toggle-fullscreen' } }
      : { state, action: { kind: 'toggle-play' } };
  }

  if (zone === 'toggle') return { state, action: { kind: 'toggle-play' } };

  // A double tap only skips when both taps were on the *same* side. Tapping left then right is a
  // user changing their mind, and −10 s followed by +10 s is a jarring way to answer that.
  if (isDouble && previous?.zone === zone) {
    return { state, action: { kind: 'skip', seconds: zone === 'back' ? -TAP_SKIP_SEC : TAP_SKIP_SEC } };
  }

  return { state, action: { kind: 'reveal' } };
}

// ---------------------------------------------------------------------------
// Swipe — the minimized player's two window acts
// ---------------------------------------------------------------------------

/**
 * How far a swipe has to travel to count, in CSS pixels. Above {@link TAP_MOVE_TOLERANCE_PX}, which
 * is the distance at which the other gesture on this page stops calling a drag a tap: shorter than
 * that is nobody moving on purpose, and both actions here are ones a reader cannot undo by repeating
 * them, so the threshold is where a *deliberate* push starts rather than where an accidental one ends.
 */
export const SWIPE_MIN_TRAVEL_PX = 48;

/**
 * How much longer the deciding axis must be than the other one. Without it a diagonal drag would be
 * decided by whichever axis won by a pixel, so the same gesture would sometimes dismiss and
 * sometimes expand — the one failure a gesture cannot recover from, because there is no way to
 * aim more carefully at a rule you cannot see.
 */
export const SWIPE_DOMINANCE = 1.5;

export interface SwipeInput {
  /** Travel from `down` to `up`, positive **rightward**. */
  readonly dx: number;
  /** Travel from `down` to `up`, positive **downward**. */
  readonly dy: number;
  /**
   * Whether a sideways push dismisses as well as a downward one — a property of the shape being
   * swiped, not of the pointer. A popover sits against the edge of the viewport and has somewhere
   * to be pushed *to*; a bar spanning the whole width does not, so on one the gesture set is three
   * directions and on the other it is two.
   */
  readonly sideways: boolean;
  /**
   * Whether an upward push has anything to open. A box with a picture in it opens back into the watch
   * route it came from, so `true` is the default and the field may be left out; the box that says the
   * queue has ended has no route to go back to, so up is not part of its gesture at all.
   *
   * A property of what the box *is*, exactly as `sideways` is a property of its shape — and read the
   * same way, through {@link usableDy}, so a direction with no act behind it cannot arm an indicator
   * the release would then refuse.
   */
  readonly liftable?: boolean;
}

/** `null` is the common answer: most drags on a video are not aimed at the window. */
export type SwipeAction = 'close' | 'maximize' | null;

/**
 * The drag's horizontal travel **as this shape can spend it**, and every rule below measures this
 * rather than `dx`.
 *
 * A direction the shape has no act for is not a weak vote for the other axis, it is *not part of the
 * gesture*: on the bar neither sideways direction means anything, and on the popover leftward is a
 * push into the page rather than off its own edge. Read as travel, those directions could only ever
 * spoil a vertical drag — the dominance rule would see a diagonal, and the reader would get nothing
 * for a gesture they made deliberately. Dropped here instead, a left-and-up drag on the popover is
 * exactly the up-drag it looks like, and so is a right-and-up drag on the bar.
 *
 * Always ≥ 0, which is what lets the dominance comparisons below drop their sign handling.
 */
function usableDx(swipe: SwipeInput): number {
  return swipe.sideways ? Math.max(0, swipe.dx) : 0;
}

/**
 * The same rule on the other axis: a downward push always dismisses, and an upward one is travel only
 * where there is something to open ({@link SwipeInput.liftable}).
 *
 * Dropped rather than counted, for `usableDx`'s reason. A box that cannot be opened would otherwise
 * read a lift as an ambiguous diagonal — nothing happens, but the reader has made a deliberate
 * gesture and gets no answer — and, worse, a straight lift would arm the maximize indicator on a box
 * whose release has no act to perform. Dropped, an up-and-right drag on such a box is the rightward
 * dismissal it looks like, and a straight lift is a drag the box quietly declines.
 *
 * Always ≥ 0, like its twin, so the dominance comparisons below need no sign handling.
 */
function usableDy(swipe: SwipeInput): number {
  if (swipe.dy > 0) return swipe.dy;
  return swipe.liftable === false ? 0 : -swipe.dy;
}

/**
 * One completed drag, read as a window act. Down closes and up expands because both name where the
 * player is going — down is off the bottom of the screen, up is back to the page it came from.
 */
export function decideSwipe(swipe: SwipeInput): SwipeAction {
  const { dx, dy } = swipe;
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null;

  const across = usableDx(swipe);
  const along = usableDy(swipe);

  if (along >= SWIPE_MIN_TRAVEL_PX && along > across * SWIPE_DOMINANCE) {
    return dy > 0 ? 'close' : 'maximize';
  }
  if (across >= SWIPE_MIN_TRAVEL_PX && across > along * SWIPE_DOMINANCE) return 'close';

  return null;
}

/**
 * The resistance the box meets the instant a lift passes {@link SWIPE_MIN_TRAVEL_PX}, and the slope
 * the soft wall below leaves that point at. Past the distance where letting go would maximize, more
 * travel says nothing new — so the box keeps answering, but slowly: a finger that has already made the
 * decision should not be able to inflate a corner player to the size of the page it is sitting on.
 */
export const SWIPE_LIFT_RUBBER = 0.35;

/**
 * The growth's **asymptote** — twice the distance that already committed the act, and a limit the box
 * approaches without ever arriving.
 *
 * It used to be a `Math.min` on a straight line, which is a soft band and then a wall: the box
 * tracked the finger at 0.35 up to 185 px of lift and then stopped dead, so a slow drag ended in a
 * picture that had quietly gone rigid under it. The curve below leaves the wall out — the resistance
 * rises smoothly instead, which is what pressing against something soft feels like — and the ceiling
 * stays exactly where it was, so nothing about how big the box can get has changed.
 */
export const SWIPE_LIFT_MAX_PX = SWIPE_MIN_TRAVEL_PX * 2;

/**
 * What is left of the box when it has been pushed far enough to dismiss it: half gone at the
 * threshold, so "let go now" is something the reader can *see* rather than a distance they have to
 * have measured.
 */
export const SWIPE_FADE_AT_THRESHOLD = 0.5;

/**
 * How sharply the fade ramps up over the first {@link SWIPE_MIN_TRAVEL_PX}. `1` is the plain
 * line this replaces, which had lost an eighth of the box by 12 px and a quarter by 24 — enough that a
 * drag somebody was still deciding about already looked like a dismissal. Squared, the same 48 px still
 * ends at {@link SWIPE_FADE_AT_THRESHOLD} and the early travel barely costs anything (97 % at 12 px,
 * 88 % at 24), so the fade starts saying something around the distance it is about.
 */
export const SWIPE_FADE_EASE = 2;

/** A ghost rather than nothing, however far the push goes — a box still under the finger is still there. */
export const SWIPE_FADE_FLOOR = 0.1;

export interface SwipeFollowInput extends SwipeInput {
  /**
   * The height of the box being dragged, in CSS pixels. The growth is expressed against it so that
   * {@link SwipeFollow.grow} is a plain multiplier the view can apply to whichever dimension its own
   * shape grows — and so the box's **top edge sits under the finger** in both shapes, which is the
   * one thing that makes an expanding box read as being pulled open rather than animated at.
   */
  readonly heightPx: number;
}

/**
 * The drag as the box should render it, mid-gesture. Four numbers and never a transform: what
 * `translate` or `width` those become is the view's, because the two shapes spend them differently —
 * the popover grows its own box, the bar grows the picture inside it.
 */
export interface SwipeFollow {
  /**
   * What releasing *now* would do — {@link decideSwipe} on the same drag, so the preview can never
   * promise an act the release refuses. The whole reason this is not a second decision.
   */
  readonly action: SwipeAction;
  /** Rightward travel to move the box by. Zero unless a sideways dismissal is what is happening. */
  readonly dx: number;
  /** Downward travel to move the box by. Zero unless a downward dismissal is what is happening. */
  readonly dy: number;
  /** ≥ 1, and 1 unless the box is being pulled open. */
  readonly grow: number;
  /** ≤ 1, and 1 unless the box is being pushed away. */
  readonly fade: number;
}

/**
 * How far the box actually opens for a lift of `lift`. One-for-one up to the threshold — the top edge
 * has to stay under the finger while the reader is still aiming — and then an exponential approach to
 * {@link SWIPE_LIFT_MAX_PX}: the resistance starts at exactly {@link SWIPE_LIFT_RUBBER}, so the
 * handover is smooth, and rises from there, so there is no distance at which the box stops answering.
 */
function softLift(lift: number): number {
  if (lift <= SWIPE_MIN_TRAVEL_PX) return lift;
  const room = SWIPE_LIFT_MAX_PX - SWIPE_MIN_TRAVEL_PX;
  const past = lift - SWIPE_MIN_TRAVEL_PX;
  return SWIPE_MIN_TRAVEL_PX + room * (1 - Math.exp((-SWIPE_LIFT_RUBBER * past) / room));
}

function growAgainst(lift: number, heightPx: number): number {
  // A box with no height cannot be grown *proportionally* to itself. Before the first layout, or on
  // the parked frame, that is the honest answer rather than a division by zero.
  if (!Number.isFinite(heightPx) || heightPx <= 0) return 1;
  return 1 + softLift(lift) / heightPx;
}

/**
 * Two segments, meeting at the threshold. Before it the fade is eased ({@link SWIPE_FADE_EASE}) so
 * that early travel costs the box almost nothing; after it the original straight line continues, so
 * the box still reaches {@link SWIPE_FADE_FLOOR} at the same distance it always did.
 */
function fadeAfter(travel: number): number {
  const slope = (1 - SWIPE_FADE_AT_THRESHOLD) / SWIPE_MIN_TRAVEL_PX;
  const spent =
    travel <= SWIPE_MIN_TRAVEL_PX
      ? (1 - SWIPE_FADE_AT_THRESHOLD) * (travel / SWIPE_MIN_TRAVEL_PX) ** SWIPE_FADE_EASE
      : 1 - SWIPE_FADE_AT_THRESHOLD + (travel - SWIPE_MIN_TRAVEL_PX) * slope;
  return Math.max(SWIPE_FADE_FLOOR, 1 - spent);
}

/**
 * The same drag, still under the finger. {@link decideSwipe} answers a completed gesture; this one
 * answers every moment of it, and the two agree by construction because this calls that.
 *
 * The dismissals follow one-for-one — a box being pushed off the screen is going exactly as far as
 * the finger takes it — while the growth rubber-bands, because a dismissal has somewhere to be and an
 * expansion does not. An **ambiguous** drag moves nothing at all: the dominant-axis rule that decides
 * the release decides this too, so a diagonal the release would ignore is a diagonal the box ignores,
 * which is the only honest way to show a rule the reader cannot see.
 */
export function followSwipe(swipe: SwipeFollowInput): SwipeFollow {
  const resting: SwipeFollow = { action: null, dx: 0, dy: 0, grow: 1, fade: 1 };
  const { dx, dy } = swipe;
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return resting;

  // The travel this shape can spend, not the travel the pointer made — see `usableDx` and
  // `usableDy`. Measuring the same numbers the release measures is what keeps the preview and the
  // act one decision.
  const across = usableDx(swipe);
  const along = usableDy(swipe);
  const action = decideSwipe(swipe);

  if (along > across * SWIPE_DOMINANCE) {
    return dy > 0
      ? { action, dx: 0, dy, grow: 1, fade: fadeAfter(dy) }
      : { action, dx: 0, dy: 0, grow: growAgainst(-dy, swipe.heightPx), fade: 1 };
  }
  if (across > 0 && across > along * SWIPE_DOMINANCE) {
    return { action, dx: across, dy: 0, grow: 1, fade: fadeAfter(across) };
  }

  return { ...resting, action };
}

// ---------------------------------------------------------------------------
// What a skip says on screen
// ---------------------------------------------------------------------------

/**
 * How long a skip stays *joinable*. Longer than {@link DOUBLE_TAP_WINDOW_MS} on purpose: the two
 * windows measure different things. That one asks whether two taps were one gesture — a threshold a
 * finger has to beat — while this one asks whether the reader is still in the middle of skipping, and
 * they are: the second press of a repeated skip is aimed at a control they are already looking at.
 * Below about this, a run of ordinary presses reads as "10 s" three times rather than as 30.
 */
export const SKIP_RUN_WINDOW_MS = 900;

/**
 * A run of skips in one direction — what the on-screen feedback counts up. Distances, not positions:
 * the transport already knows where the video is, and a run outliving one seek would otherwise have
 * to be reconciled with it.
 */
export interface SkipRun {
  readonly direction: 'back' | 'forward';
  /** The run's total so far, in seconds and always positive — the direction is its own field. */
  readonly totalSec: number;
  /** When the last press landed, so the next one can ask whether it joins this run. */
  readonly atMs: number;
  /**
   * When the run's **first** press landed — the run's identity, which is a different question from
   * `atMs` and the one the on-screen feedback needs. A view that keyed its element on the last
   * press re-mounted it on every one, restarting the fade from nothing: a quick run of taps read as a
   * disc flashing rather than as a counter going up. Keyed on this it is one element for the whole run,
   * and still a *new* one for the next run — including the case the last press cannot distinguish, a
   * fresh run starting after the joinable window but before the disc has finished fading, where the
   * wording repeats and only the start time has moved.
   */
  readonly startedAtMs: number;
}

/**
 * One skip, folded into the run it belongs to. A press joins when it goes the **same way** within
 * {@link SKIP_RUN_WINDOW_MS} and starts a fresh run otherwise — including a reversal inside the
 * window, which is a reader correcting an overshoot: "+10 s" then "−10 s" nets to nothing, and a
 * counter that showed 0 s would be describing a seek that did happen.
 *
 * A zero-second press is not a skip and cannot start or extend a run; the caller gets the previous
 * one back unchanged (`null` included), so nothing on screen flickers for a command that moved
 * nothing.
 */
export function accumulateSkip(
  previous: SkipRun | null,
  press: { readonly seconds: number; readonly timeMs: number },
): SkipRun | null {
  if (!Number.isFinite(press.seconds) || press.seconds === 0) return previous;

  const direction: SkipRun['direction'] = press.seconds < 0 ? 'back' : 'forward';
  const step = Math.abs(press.seconds);
  const joins =
    previous !== null &&
    previous.direction === direction &&
    press.timeMs - previous.atMs <= SKIP_RUN_WINDOW_MS;

  return {
    direction,
    totalSec: joins ? previous.totalSec + step : step,
    atMs: press.timeMs,
    startedAtMs: joins ? previous.startedAtMs : press.timeMs,
  };
}
